import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const VK_CLIENT_ID = '54402932';
const VK_REDIRECT_URI = `${window.location.origin}/auth/vk/callback`;

const isHttps = window.location.protocol === 'https:';
const showVK = VK_CLIENT_ID && isHttps;

const YANDEX_CLIENT_ID = '287163414da74edd908e8c1832619465';
const YANDEX_REDIRECT_URI = `${window.location.origin}/auth/yandex/callback`;

const SocialAuthButtons = () => {
  const navigate = useNavigate();
  const [vkLoading, setVkLoading] = useState(false);
  const vkSdkReady = useRef(false);

  const yandexAuthUrl = `https://oauth.yandex.com/authorize?response_type=code&client_id=${YANDEX_CLIENT_ID}&redirect_uri=${encodeURIComponent(YANDEX_REDIRECT_URI)}`;

  useEffect(() => {
    if (!showVK || vkSdkReady.current) return;
    
    // Загружаем VK ID SDK
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@vkid/sdk@<3.0.0/dist-sdk/umd/index.js';
    script.async = true;
    script.onload = () => {
      if (window.VKIDSDK) {
        const VKID = window.VKIDSDK;
        // Инициализация конфига SDK
        VKID.Config.init({
          app: parseInt(VK_CLIENT_ID),
          redirectUrl: VK_REDIRECT_URI,
          responseMode: VKID.ConfigResponseMode.Callback,
          source: VKID.ConfigSource.LOWCODE,
          scope: 'email',
        });
        vkSdkReady.current = true;
        console.log('VK SDK initialized');
      }
    };
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  // Обработчик клика по кнопке VK - согласно документации
  const handleVKClick = async () => {
    if (!window.VKIDSDK || !vkSdkReady.current) {
      console.error('VK SDK not ready');
      return;
    }
    
    setVkLoading(true);
    const VKID = window.VKIDSDK;

    try {
      // Открываем окно авторизации VK - возвращает Promise с code и device_id
      console.log('Opening VK auth...');
      const authResult = await VKID.Auth.login();
      console.log('VK Auth result:', authResult);
      
      const { code, device_id } = authResult;
      
      // Обмениваем код на токены через SDK метод
      console.log('Exchanging code for tokens...');
      const tokenData = await VKID.Auth.exchangeCode(code, device_id);
      console.log('VK Token data:', tokenData);
      
      // Отправляем access_token на наш бэкенд
      const response = await fetch('/api/auth/vk/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: tokenData.access_token })
      });
      
      const result = await response.json();
      
      if (result.access_token) {
        localStorage.setItem('access_token', result.access_token);
        if (result.refresh_token) {
          localStorage.setItem('refresh_token', result.refresh_token);
        }
        navigate('/profile');
        window.location.reload();
      } else {
        console.error('Backend auth failed:', result);
        alert('Ошибка авторизации: ' + (result.error || 'Неизвестная ошибка'));
      }
    } catch (err) {
      console.error('VK auth error:', err);
      // Не показываем ошибку если пользователь просто закрыл окно
      if (err && err.message && !err.message.includes('closed')) {
        alert('Ошибка авторизации VK');
      }
    } finally {
      setVkLoading(false);
    }
  };

  return (
    <div className="w-full flex gap-3 items-stretch">
      {showVK && (
        <button
          onClick={handleVKClick}
          disabled={vkLoading}
          className="VkIdWebSdk__button VkIdWebSdk__button_reset flex-1"
          style={{
            border: 'none',
            margin: 0,
            padding: 0,
            background: '#0077ff',
            cursor: vkLoading ? 'wait' : 'pointer',
            borderRadius: '8px',
            minHeight: '44px',
            opacity: vkLoading ? 0.7 : 1,
            transition: 'all 0.1s ease-out',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M4.54648 4.54648C3 6.09295 3 8.58197 3 13.56V14.44C3 19.418 3 21.907 4.54648 23.4535C6.09295 25 8.58197 25 13.56 25H14.44C19.418 25 21.907 25 23.4535 23.4535C25 21.907 25 19.418 25 14.44V13.56C25 8.58197 25 6.09295 23.4535 4.54648C21.907 3 19.418 3 14.44 3H13.56C8.58197 3 6.09295 3 4.54648 4.54648ZM6.79999 10.15C6.91798 15.8728 9.92951 19.31 14.8932 19.31H15.1812V16.05C16.989 16.2332 18.3371 17.5682 18.8875 19.31H21.4939C20.7869 16.7044 18.9535 15.2604 17.8141 14.71C18.9526 14.0293 20.5641 12.3893 20.9436 10.15H18.5722C18.0747 11.971 16.5945 13.6233 15.1803 13.78V10.15H12.7711V16.5C11.305 16.1337 9.39237 14.3538 9.314 10.15H6.79999Z" fill="white"/>
              </svg>
            </div>
            <div style={{
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              color: '#ffffff',
              fontFamily: '-apple-system, system-ui, "Helvetica Neue", Roboto, sans-serif',
              fontSize: '15px',
              fontWeight: 500,
              marginLeft: '-28px',
            }}>
              <span className="hidden sm:inline">{vkLoading ? 'Загрузка...' : 'Войти с VK ID'}</span>
              <span className="sm:hidden">{vkLoading ? 'Загрузка...' : 'VK'}</span>
            </div>
          </div>
        </button>
      )}

      {YANDEX_CLIENT_ID && (
        <a
          href={yandexAuthUrl}
          className="flex-1 flex items-center justify-center gap-2 px-4 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-base shadow-md hover:shadow-lg transition-all duration-200 h-[44px]"
        >
          <span className="text-lg font-bold">Я</span>
          Яндекс
        </a>
      )}
    </div>
  );
};

export default SocialAuthButtons;
